import { Link, useNavigate, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import { newsCategoryMeta } from "@/mocks/categories";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const item = news.find((entry) => entry.id === id);
  const meta = item ? newsCategoryMeta[item.category] : null;

  useSEO({
    title: item ? `${item.title} - 影视资讯 - 星光传媒` : "资讯未找到 - 星光传媒",
    description: item ? item.summary : "抱歉，未找到对应的资讯内容。",
    keywords: item ? `${item.title},星光传媒,影视资讯,${meta?.label ?? ""}` : "星光传媒,影视资讯",
  });

  if (!item || !meta) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-article-line"
          title="没有找到这条资讯"
          desc="你访问的资讯可能已被移除或链接有误，去看看其他最新内容吧。"
          action={
            <Link
              to="/news/industry"
              className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              查看行业动态
            </Link>
          }
        />
      </div>
    );
  }

  const related = news
    .filter((entry) => entry.id !== item.id && entry.category === item.category)
    .slice(0, 3);
  const fallbackRelated = news.filter((entry) => entry.id !== item.id).slice(0, 3);
  const recommendations = related.length >= 2 ? related : fallbackRelated;

  return (
    <article className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news/industry" },
          { label: meta.label, to: `/news/${item.category}` },
          { label: item.title },
        ]}
      />

      <div className="mt-6 flex flex-col gap-10 lg:flex-row lg:gap-12">
        <div className="min-w-0 flex-1">
          <span className="inline-flex rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
            {meta.label}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {item.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line" />
              来源：{item.source}
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg bg-secondary-100">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 星光传媒资讯`}
              className="aspect-[16/9] w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 flex flex-col gap-5">
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <button
              type="button"
              onClick={() => navigate(`/news/${item.category}`)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{meta.label}
            </button>
            <Link
              to="/news/industry"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
            >
              浏览全部资讯
            </Link>
          </div>
        </div>

        <aside className="w-full shrink-0 lg:w-80">
          <div className="sticky top-24">
            <h2 className="mb-4 font-heading text-base font-semibold text-foreground-950">
              相关文章
            </h2>
            <div className="flex flex-col gap-3">
              {recommendations.map((entry) => (
                <Link
                  key={entry.id}
                  to={`/detail/news/${entry.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300"
                >
                  <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                    <img
                      src={entry.cover}
                      alt={entry.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-accent-600">
                      {entry.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">{entry.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}